import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuid } from 'uuid';

export default function ScriptRundown(props) {
 return <RundownEditor key={props.channelId} {...props} />;
}

/**
 * The callbacks are read through a ref and the load effect depends on the channel only: the page
 * re-renders every second, and callers that pass fresh function identities used to make this effect
 * refetch (and overwrite unsaved edits) on every one of those renders.
 */
function RundownEditor({ channelId, listScripts, getRundown, saveRundown, active = false, scriptAdded = null }) {
 const [saved, setSaved] = useState(null);
 const [entries, setEntries] = useState([]);
 const [scripts, setScripts] = useState([]);
 const [selection, setSelection] = useState('');
 const [saving, setSaving] = useState(false);
 const [conflict, setConflict] = useState(false);
 const [error, setError] = useState('');
 const [loading, setLoading] = useState(true);
 const [loadError, setLoadError] = useState('');
 const [attempt, setAttempt] = useState(0);
 const [confirmReload, setConfirmReload] = useState(false);
 const [removed, setRemoved] = useState(null);
 const [notice, setNotice] = useState('');
 const api = useRef({});
 api.current = { listScripts, getRundown, saveRundown };
 const dirty = Boolean(saved) && JSON.stringify(saved.entries) !== JSON.stringify(entries);
 const dirtyRef = useRef(false);
 dirtyRef.current = dirty;

 useEffect(() => {
  let current = true;
  setLoading(true); setLoadError('');
  Promise.all([api.current.listScripts(), api.current.getRundown(channelId)])
   .then(([available, rundown]) => {
    if (!current) return;
    setScripts(available);
    setSaved(rundown);
    setEntries(previous => (dirtyRef.current ? previous : rundown.entries));
   })
   .catch(() => { if (current) setLoadError("Couldn't load the rundown or the saved scripts. Try again."); })
   .finally(() => { if (current) setLoading(false); });
  return () => { current = false; };
 }, [channelId, attempt]);

 // A script was just imported here: refresh the pickable list and preselect it, so one click puts it in the rundown.
 useEffect(() => {
  if (!scriptAdded) return undefined;
  let current = true;
  api.current.listScripts()
   .then(list => {
    if (!current) return;
    setScripts(list);
    const index = list.findIndex(item => item.script_id === scriptAdded.script_id && item.version === scriptAdded.version);
    if (index >= 0) setSelection(String(index));
    setNotice(`Saved ${scriptAdded.title || 'the script'}. Choose Add script to put it in this rundown.`);
   })
   .catch(() => { if (current) setError("Saved, but couldn't refresh the script list. Choose Refresh available scripts."); });
  return () => { current = false; };
 }, [scriptAdded?.token]);  // eslint-disable-line react-hooks/exhaustive-deps

 const titleOf = item => scripts.find(script => script.script_id === item.script_id)?.title || item.script_id;
 function move(index, offset) {
  const next = [...entries];
  [next[index], next[index + offset]] = [next[index + offset], next[index]];
  setEntries(next); setRemoved(null);
 }
 function remove(index) {
  setRemoved({ item: entries[index], index });
  setEntries(entries.filter((_, i) => i !== index));
 }
 function undoRemove() {
  const next = [...entries];
  next.splice(Math.min(removed.index, next.length), 0, removed.item);
  setEntries(next); setRemoved(null);
 }
 async function save() {
  setSaving(true); setError('');
  try {
   const result = await api.current.saveRundown(channelId, { expected_revision: saved.revision, entries: entries.map(item => ({ ...item })) });
   setSaved(result); setEntries(result.entries); setRemoved(null);
  } catch (failure) {
   const stale = failure?.status === 409 || failure?.response?.status === 409;
   setConflict(stale);
   setError(stale ? "Someone else changed this rundown. Your edits are kept here. Reload to see their version." : "Couldn't save. Your changes are kept here, so you can try again.");
  } finally { setSaving(false); }
 }
 async function reload() {
  setConfirmReload(false); setSaving(true);
  try {
   const result = await api.current.getRundown(channelId);
   setSaved(result); setEntries(result.entries); setConflict(false); setError(''); setRemoved(null);
  } catch (failure) { setError("Couldn't reload the rundown. Your edits are kept here."); }
  finally { setSaving(false); }
 }

 return <Stack spacing={1.5}>
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
   <Typography variant="h6" component="h2">Script rundown</Typography>
   {saved && <Chip size="small" label={`Saved revision: ${saved.revision}`} />}
   {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}
  </Stack>
  <Typography variant="body2">
   {active
    ? "Saving updates the rundown only. The script on air doesn't change until you start script following again."
    : "This rundown is saved, but script following is off. Saving won't change what is on air."}
  </Typography>
  {notice && <Alert severity="success" role="status" onClose={() => setNotice('')}>{notice}</Alert>}
  {loading && <Typography role="status">Loading rundown…</Typography>}
  {loadError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setAttempt(attempt + 1)}>Retry</Button>}>{loadError}</Alert>}
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
   <label>Script version to add{' '}
    <select value={selection} disabled={!saved || saving} onChange={event => setSelection(event.target.value)}>
     <option value="">Choose a saved script</option>
     {scripts.map((item, index) => <option key={`${item.script_id}:${item.version}`} value={index}>{item.title} — version {item.version}</option>)}
    </select>
   </label>
   <Button variant="outlined" size="small" disabled={!saved || saving || selection === ''} onClick={() => {
    const item = scripts[Number(selection)];
    setEntries([...entries, { entry_id: uuid(), script_id: item.script_id, version: item.version }]);
    setRemoved(null); setNotice('');
   }}>Add script</Button>
   <Button size="small" disabled={saving} onClick={async () => {
    // Refresh only the pickable versions; pinned rundown entries and unsaved
    // edits (including their captured versions) are deliberately preserved.
    try { setScripts(await api.current.listScripts()); } catch (failure) { setError("Couldn't refresh the script list."); }
   }}>Refresh available scripts</Button>
  </Stack>
  {saved && !entries.length && <Typography variant="body2">No scripts yet. Add a saved script to build the rundown.</Typography>}
  <ol aria-label="Rundown entries" style={{ margin: 0, paddingLeft: '1.4em' }}>{entries.map((item, index) => <li key={item.entry_id}>
   <Stack direction="row" alignItems="center" spacing={0.5}>
    <Box sx={{ flexGrow: 1 }}>{titleOf(item)} — version {item.version}</Box>
    <IconButton size="small" aria-label={`Move entry ${index + 1} up`} disabled={saving || index === 0} onClick={() => move(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
    <IconButton size="small" aria-label={`Move entry ${index + 1} down`} disabled={saving || index === entries.length - 1} onClick={() => move(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
    <IconButton size="small" aria-label={`Remove entry ${index + 1}`} disabled={saving} onClick={() => remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
   </Stack>
  </li>)}</ol>
  {removed && <Alert severity="info" role="status" action={<Button color="inherit" size="small" onClick={undoRemove}>Undo</Button>}>Removed {titleOf(removed.item)} — version {removed.item.version}.</Alert>}
  {error && <Typography role="alert" color="error">{error}</Typography>}
  {conflict && <Button disabled={saving} onClick={() => setConfirmReload(true)}>Reload saved rundown</Button>}
  {confirmReload && <Stack role="group" aria-label="Confirm discarding unsaved edits">
   <Typography>Reloading discards your unsaved edits.</Typography>
   <Button onClick={reload}>Reload</Button>
   <Button onClick={() => setConfirmReload(false)}>Keep editing</Button>
  </Stack>}
  <Box sx={{ position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.paper', py: 1 }}>
   <Stack direction="row" spacing={1}>
    <Button variant="contained" disabled={!dirty || saving || conflict} onClick={save}>Save rundown</Button>
    <Button disabled={!dirty || saving} onClick={() => { setEntries(saved.entries); setRemoved(null); }}>Discard changes</Button>
   </Stack>
  </Box>
 </Stack>;
}
